import db from "../db/database";
import {
  fetchPagedFromBusinessCentral,
  parseImportResponse,
  sendToBusinessCentral,
} from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { marcaConSolape } from "./margenDeSolape";
import { formatDateForBC } from "../utils/dateUtils";
import { getPendingSync, isOnline } from "./syncService";
import {
  conAvisoDeBajaRechazada,
  enviarLote,
  motivoDeGuardadoFallido,
  resumenDelLote,
  separarPendientes,
} from "./loteSyncHelpers";
import {
  anotarAvanceDeLectura,
  anotarError,
  anotarFila,
  anotarInicioDeLote,
  anotarLectura,
  anotarResumenDelLote,
} from "./syncLog";

// Nombre con el que esta entidad aparece en las líneas de consola.
const ENTIDAD = "colegios";

// Función para transformar un colegio al formato esperado por Business Central.
//
// Los campos más allá de Nombre usan los mismos nombres que devuelve el GET de
// Colegios (Ciudad, Provincia, Teléfono, Email, Contacto, Fecha_Alta) -- mismo
// criterio que ya se usó con Tienda/Grupo en userSyncService. Falta confirmar
// con BC que Importaciones_insertaColegios los acepte con esos nombres: si no,
// el alta y la edición guardarían sólo el nombre y el resto se perdería en el
// viaje, sin ningún error visible.
const transformColegioForBC = (colegio) => {
  // Transformar el colegio al formato esperado por BC
  const transformedColegio = {
    No: colegio.No || "TEMP", // Si no tiene No, usamos TEMP para que BC asigne uno
    Nombre: colegio.nombre,
    Ciudad: colegio.Ciudad || "",
    Provincia: colegio.Provincia || "",
    Teléfono: colegio.Teléfono || "",
    Email: colegio.Email || "",
    Contacto: colegio.Contacto || "",
    Fecha_Alta: formatDateForBC(colegio.Fecha_Alta),
    Deleted: colegio.deleted,
  };

  // Crear el formato final esperado por el endpoint
  return {
    data: JSON.stringify({ Colegios: [transformedColegio] }),
  };
};

// Payload con todos los colegios que se manden en la misma llamada.
// Importaciones_insertaColegios recorre el array y procesa cada colegio aislado
// en TryProcesarColegio, así que un rechazo no arrastra a los demás.
const transformColegiosForBC = (colegios) => ({
  data: JSON.stringify({
    Colegios: colegios.map(
      (colegio) => JSON.parse(transformColegioForBC(colegio).data).Colegios[0]
    ),
  }),
});

// Nombre con el que se identifica al colegio en el cuadro de resultados.
const nombreDeColegio = (colegio) => colegio.nombre || colegio.No || "(sin nombre)";

// Campos que BC expone en la entidad Colegios. Antes sólo se guardaban No y
// nombre, y el resto se descartaba en cada sincronización aunque BC ya los
// mandara -- por eso la vista de un colegio no tenía nada más que mostrar.
const mapServerColegioToLocal = (serverColegio) => ({
  No: serverColegio.No,
  nombre: serverColegio.Nombre,
  Ciudad: serverColegio.Ciudad || "",
  Provincia: serverColegio.Provincia || "",
  Teléfono: serverColegio.Teléfono || "",
  Email: serverColegio.Email || "",
  Contacto: serverColegio.Contacto || "",
  Fecha_Alta: serverColegio.Fecha_Alta || null,
  Source_Counter: serverColegio.Source_Counter ?? 0,
  lastModified: new Date(),
  lastSynced: new Date(),
});

// BC rechazó la baja: se revierte el borrado local para que el colegio vuelva a
// aparecer en el listado y local y BC no queden divergentes. Mismo criterio que
// en userSyncService, turnoSyncService, tpvSyncService y clientSyncService.
const restoreDeletedColegio = async (colegio, reason) => {
  await db.colegios.update(colegio.id, {
    deleted: false,
    pendingSync: 0,
    lastModified: new Date(),
    // El motivo que devolvió BC se guarda en el propio registro. Es lo único
    // que sobrevive a la corrida: el registro reaparece en el listado con
    // pendingSync 0, o sea con aspecto de estar perfectamente sincronizado, y
    // sin esto no queda nadie que pueda explicar por qué volvió. Lo lee
    // RecordSyncTag. Ver SANTPV-59.
    syncError: reason || "Business Central no aplicó la baja.",
  });
};

// Sincroniza un solo colegio contra BC, para el botón de sincronizar de cada
// fila del listado. Devuelve { ok, message }, igual que los demás maestros.
export const syncColegioById = async (colegioId) => {
  if (!isOnline()) {
    return { ok: false, message: "No hay conexión a internet" };
  }

  try {
    const colegio = await db.colegios.get(colegioId);
    if (!colegio) {
      return { ok: false, message: "Colegio no encontrado" };
    }

    if (colegio.deleted) {
      // Nunca llegó a BC: sólo existe en local.
      if (!colegio.No) {
        await db.colegios.delete(colegio.id);
        return { ok: true, message: "Colegio eliminado" };
      }

      const response = await sendToBusinessCentral(
        "Importaciones_insertaColegios",
        transformColegioForBC(colegio),
        true
      );
      const result = parseImportResponse(response, colegio.No);

      if (!result.ok) {
        await restoreDeletedColegio(colegio, result.message);
        return {
          ok: false,
          message:
            result.message || "Business Central no aplicó la baja. El colegio sigue en el listado.",
        };
      }

      await db.colegios.delete(colegio.id);
      return { ok: true, message: "Colegio dado de baja en Business Central" };
    }

    const response = await sendToBusinessCentral(
      "Importaciones_insertaColegios",
      transformColegioForBC(colegio),
      true
    );
    const result = parseImportResponse(response, colegio.No || "TEMP");

    if (!result.ok || !result.assignedNo) {
      return {
        ok: false,
        message: result.message || "Respuesta inválida de Business Central",
      };
    }

    // Se lee antes del update, que es justo lo que lo borra.
    const teniaBajaRechazada = Boolean(colegio.syncError);

    await db.colegios.update(colegio.id, {
      No: result.assignedNo,
      lastModified: new Date(),
      lastSynced: new Date(),
      pendingSync: 0,
      syncError: null,
    });

    return {
      ok: true,
      message: conAvisoDeBajaRechazada("Colegio sincronizado correctamente.", teniaBajaRechazada),
    };
  } catch (error) {
    anotarError(ENTIDAD, `falló el envío del registro ${colegioId}`, error);
    return {
      ok: false,
      message: formatAxiosError(error, "No se pudo sincronizar el colegio"),
    };
  }
};

export const syncColegios = async () => {
  if (!isOnline()) {
    return false;
  }

  // Colegios cuyo push a BC falló (o fue ignorado por BC) en esta corrida.
  // Cada entrada es { colegio, action: "delete" | "save", reason }. Antes los
  // errores sólo se escribían en consola y la función devolvía true igual.
  const failedColegios = [];

  // Una fila por registro enviado, para el cuadro que se le muestra a quien
  // apretó Sincronizar. Incluye los correctos, no sólo los fallos.
  const detalle = [];

  try {
    const pendingColegios = await getPendingSync("colegios");

    if (pendingColegios.length > 0) {
      const { soloLocales, aEnviar } = separarPendientes(pendingColegios);

      for (const colegio of soloLocales) {
        await db.colegios.delete(colegio.id);
        detalle.push(
          anotarFila(
            ENTIDAD,
            {
              registro: nombreDeColegio(colegio),
              no: null,
              accion: "deleted",
              ok: true,
              mensaje: "Eliminado sólo en local: nunca llegó a Business Central",
            },
            colegio.id,
            "baja-local"
          )
        );
      }

      if (aEnviar.length > 0) {
        anotarInicioDeLote(ENTIDAD, aEnviar.length);

        // UNA llamada para todos los pendientes, en vez de una por registro.
        const lote = await enviarLote(
          "Importaciones_insertaColegios",
          transformColegiosForBC(aEnviar),
          aEnviar.length,
          "colegios"
        );

        // El cruce es por posición: BC devuelve los resultados en el mismo orden
        // en que recibió los registros. Por No_ no se podría, porque todas las
        // altas se mandan como "TEMP" y volverían indistinguibles entre sí.
        for (let indice = 0; indice < aEnviar.length; indice++) {
          const colegio = aEnviar[indice];
          const resultado = lote.results[indice];
          let traiaBajaRechazada = false;

          if (colegio.deleted) {
            if (resultado.ok) {
              await db.colegios.delete(colegio.id);
            } else {
              // BC no aplicó la baja: el registro NO se borra en local, se
              // revierte el borrado y el motivo queda guardado en él.
              await restoreDeletedColegio(colegio, resultado.message);
              failedColegios.push({ colegio, action: "delete", reason: resultado.message });
            }
          } else if (resultado.ok && resultado.assignedNo) {
            // Se anota antes del update, que es lo que borra el motivo anterior.
            traiaBajaRechazada = Boolean(colegio.syncError);

            await db.colegios.update(colegio.id, {
              No: resultado.assignedNo,
              lastModified: new Date(),
              lastSynced: new Date(),
              pendingSync: 0,
              syncError: null,
            });
          } else {
            const motivo = motivoDeGuardadoFallido(resultado);
            failedColegios.push({ colegio, action: "save", reason: motivo });
          }

          detalle.push(
            anotarFila(
              ENTIDAD,
              {
                registro: nombreDeColegio(colegio),
                no: resultado.assignedNo || colegio.No || null,
                accion: resultado.action || (colegio.deleted ? "deleted" : "saved"),
                ok: colegio.deleted ? resultado.ok : resultado.ok && Boolean(resultado.assignedNo),
                mensaje: conAvisoDeBajaRechazada(resultado.message || "", traiaBajaRechazada),
              },
              colegio.id,
              colegio.deleted ? "baja" : undefined
            )
          );
        }
      }
    }

    const localColegios = await db.colegios.toArray();

    // Con colchón, y sin `sort` restando: ver `margenDeSolape.js`. Un
    // contador que retrocede deja la fila por debajo de la marca y no vuelve
    // nunca, y un contador ausente convertía el comparador en `NaN`.
    const lastSourceCode = marcaConSolape(localColegios);

    // Mapa para buscar colegios locales por No rápidamente
    const localColegiosByNo = {};
    localColegios.forEach((colegio) => {
      if (colegio.No) {
        localColegiosByNo[colegio.No] = colegio;
      }
    });

    // Devuelve el registro a guardar, o null si no hay que tocarlo: un colegio
    // con cambios locales sin sincronizar no se pisa con lo de BC.
    const buildColegioRecord = (serverColegio) => {
      const localColegio = localColegiosByNo[serverColegio.No];
      const mapped = mapServerColegioToLocal(serverColegio);

      if (localColegio) {
        if (localColegio.pendingSync !== 0) return null;
        return { ...localColegio, ...mapped, id: localColegio.id };
      }

      return { ...mapped, pendingSync: 0, deleted: false };
    };

    // Se usa "gt" desde que el contador de Colegios es único por registro.
    //
    // Hasta la extensión 1.0.4.29 no lo era: 4.117 colegios compartían sólo 319
    // valores distintos y 1.663 estaban en cero, porque el suscriptor de alta
    // asignaba el contador sin hacer Modify y el valor se perdía. Mientras duró
    // eso acá se usaba "ge", que vuelve a traer el registro empatado en el
    // máximo -- una red de seguridad parcial, porque los contadores repetidos
    // por debajo del máximo seguían sin poder recuperarse desde el cliente.
    //
    // BC lo corrigió en el origen (1.0.4.30) y renumeró lo existente con
    // InicializaSourceCounterTPV y DepuraSourceCounterDuplicadosTPV. Medido
    // contra el ambiente el 21/08/2026: 4.117 registros, 4.117 valores
    // distintos, ninguno en cero. La renumeración fue hacia arriba, así que los
    // registros que cambiaron quedaron por encima del máximo que cada terminal
    // ya tenía y entran solos en la primera sincronización -- no hace falta
    // reiniciar el checkpoint ni forzar una descarga completa.
    //
    // Y se pagina por esa misma clave en vez de por desplazamiento. Antes no se
    // podía: con 319 valores distintos para 4.117 colegios, el "gt" de cada
    // bloque se habría saltado todas las filas empatadas.
    const traidos = await fetchPagedFromBusinessCentral("Colegios", {
      filter: lastSourceCode ? `Source_Counter gt ${lastSourceCode}` : "",
      keyField: "Source_Counter",
      onPage: async (page, procesados) => {
        const records = page.map(buildColegioRecord).filter(Boolean);
        if (records.length) await db.colegios.bulkPut(records);
        anotarAvanceDeLectura(ENTIDAD, procesados);
      },
    });

    anotarLectura(ENTIDAD, { guardados: traidos, incremental: Boolean(lastSourceCode) });

    // Cierra el lote en consola con el mismo recuento que ve quien apretó

    // Sincronizar. Antes había que sumar las líneas a ojo.

    const resumen = resumenDelLote(detalle);

    anotarResumenDelLote(ENTIDAD, resumen);

    if (failedColegios.length > 0) {
      const deletes = failedColegios.filter((f) => f.action === "delete");
      const saves = failedColegios.filter((f) => f.action === "save");

      const parts = [];
      if (deletes.length > 0) {
        parts.push(
          `${deletes.length} colegio(s) no se pudieron eliminar en Business Central y siguen en el listado`
        );
      }
      if (saves.length > 0) {
        parts.push(`${saves.length} colegio(s) no se pudieron guardar en Business Central`);
      }

      // Motivo concreto que devolvió BC, si lo hubo.
      const reason = failedColegios.find((f) => f.reason)?.reason;

      return {
        success: false,
        failedCount: failedColegios.length,
        message: reason ? `${parts.join(". ")}: ${reason}` : parts.join(". "),
        lote: resumen,
      };
    }

    // Se devuelve el detalle también cuando todo salió bien: la pantalla muestra
    // el mismo cuadro en los dos casos, y `executeWithRetry` sólo mira
    // `success === false`, así que un objeto acá no cambia nada para syncAll.
    return { success: true, lote: resumen };
  } catch (error) {
    anotarError(ENTIDAD, "falló la sincronización", error);
    return false;
  }
};
